import { faFileUpload } from "@fortawesome/pro-regular-svg-icons/faFileUpload";
import { faImage } from "@fortawesome/pro-regular-svg-icons/faImage";
import { faLink } from "@fortawesome/pro-regular-svg-icons/faLink";
import { faPaperclip } from "@fortawesome/pro-regular-svg-icons/faPaperclip";
import { faPlus } from "@fortawesome/pro-regular-svg-icons/faPlus";
import { faVideo } from "@fortawesome/pro-regular-svg-icons/faVideo";
import { faTrash } from "@fortawesome/pro-solid-svg-icons/faTrash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { LinearProgress, Theme } from '@mui/material';
import { createStyles, withStyles, WithStyles } from "@mui/styles";
import * as pubApi from "api/Knowledge";
import { WidePicture } from "assets/appConfig";
import { GlobalClassKeys, ImageListStyleClasses } from 'assets/jss/common-styles';
import classNames from 'classnames';
import ButtonCancel from 'components/Buttons/ButtonCancel';
import { UploadCallback, UploadRemoveCallback, UploadStatusChange } from 'components/FileUpload/AttachSelection';
import { ImageSelectionDialog } from 'components/ImageHandler';
import { ImageSelectionResult } from 'components/ImageHandler/ImageSelectionDialog';
import MuiRegularButton from 'components/MuiExtras/Button/MuiRegularButton';
import { GridEx } from 'components/MuiExtras/Grid/GridEx';
import MuiClickable from 'components/MuiExtras/MuiClickable';
import MuiConfirmation from 'components/MuiExtras/MuiConfirmation';
import MuiColor from 'components/MuiExtras/Typography/MuiColor';
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import { AsyncUploader } from "lib/AsyncUploader";
import MediaUtils from 'lib/MediaUtils';
import * as LinkifyIt from "linkify-it";
import { AspectRatio, PublicationType } from 'models/ApiEnums';
import { ApiException, AsyncUploadModel, ContentMetadata, ContentMetadataGetDto, FileThumbnaildGetDto } from 'models/ApiModels';
import { AttachType } from 'models/AppModels';
import * as React from 'react';
import Dropzone from 'react-dropzone';
import { WithTranslation, withTranslation } from "react-i18next";
import { setTimeout } from 'timers';
import RenderFile from 'views/Publication/Components/RenderFile';
import RenderMetadata from 'views/Publication/Components/RenderMetadata';
import RenderVideo from 'views/Publication/Components/RenderVideo';
import PublicationSelectionDrawer from 'views/Publication/Dialogs/PublicationSelectionDrawer';
import { getPublicationUrl } from 'views/Timeline/Components/FeedHelpers';

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const styles = (theme: Theme) => createStyles({

    container: {
        lineHeight: "32px"
    },

    dropzone: {
        borderStyle: "dashed",
        borderWidth: 1,
        borderColor: theme.palette.grey[200],
        paddingLeft: 10,
        paddingRight: 10,
        "& > input[type=file]": {
            display: "none"
        }
    },

    progressBar: {
        width: "200px"
    },

    ...ImageListStyleClasses(theme),

});

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export function searchLink(text: string): Promise<ContentMetadata> {
    const linkify = new LinkifyIt();

    const matches = linkify.match(text);

    return new Promise<ContentMetadata>((resolve) => {

        if (matches == null || matches.length == 0)
            resolve(null);
        else {
            pubApi.getMetadataFromUrl(matches[0].url)
                .then((meta) => {
                    resolve(meta);
                })
                .catch(() => {
                    resolve(null);
                });
        }

    });
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    isLoading: boolean;
    progressBar?: number;
    lastFileUploaded?: AsyncUploadModel;
    dialog: any;

}

interface IProps {
    acceptedTypes: AttachType[];
    attach: FileThumbnaildGetDto[] | ContentMetadataGetDto;
    dropzoneText?: string;
    maxAttachFiles?: number;
    publicationId: string;
    readOnly?: boolean;
    selectedType: AttachType;
    additionalLeftButton?: React.ReactElement[];

    onUploadStatusChange?: UploadStatusChange;
    onUploadComplete?: UploadCallback;
    onUploadRemove?: UploadRemoveCallback;
    onMetadataReceived?: (data: ContentMetadataGetDto) => void;
    onPool?: () => void;
}

type Props = IProps & WithStyles<typeof styles> & WithSnackbar & WithTranslation;

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////

class HandleAttach extends React.Component<Props, IState> {

    private fileInputRef: HTMLInputElement;
    private addBoxRef: HTMLElement;

    private asyncUploader: AsyncUploader;

    constructor(props: Props) {
        super(props);
        this.state = { isLoading: false, lastFileUploaded: null, progressBar: null, dialog: null };

        this.asyncUploader = new
            AsyncUploader({
                onFileUploadProgress: this.loadAttachProgress,
                onFileUploadSuccess: this.loadAttachSuccess,
                onFileUploadError: this.loadAttachError,
                onFileStarted: this.loadAttachStarted,
            });
    }

    static defaultProps = {
        readOnly: false
    };

    ///
    private renderAlbumAddImage() {
        const { classes } = this.props;

        return (
            <div className={classes.imageListItemOuter} ref={(ref) => this.addBoxRef = ref}>
                <div className={classes.imageListItemInner}>
                    <GridEx container className={classes.imageListImageContainer} alignContent="center" alignItems="center" direction="column">
                        <GridEx item >
                            <span>{this.props.t("Views.Publication.Components.HandleAttach.AddAlbumImage")}</span>
                        </GridEx>
                        <GridEx item xs>
                            <GridEx container direction="column" justifyContent="center" fullHeight>
                                <GridEx item >
                                    <MuiClickable onClick={() => { this.handleImageSelection(); }}>
                                        <FontAwesomeIcon icon={faPlus} fixedWidth size="3x" />
                                    </MuiClickable>
                                </GridEx>
                            </GridEx>
                        </GridEx>
                    </GridEx>
                </div>
            </div>
        );
    }

    ///
    private renderImageAlbum() {
        const { classes, maxAttachFiles, onUploadRemove } = this.props;
        var files = this.props.attach as FileThumbnaildGetDto[];
        return (
            <div className={classNames(classes.imageListBox, classes.imageListOverflow, GlobalClassKeys.thinScroll, GlobalClassKeys.clearfix)}>
                {
                    files.filter(p => !p.mainFile.isEmbed).map((v) => {
                        var f = v.thumbnail ?? v.mainFile;
                        return (
                            <div key={f.fileId} className={classes.imageListItemOuter}>
                                <div className={classes.imageListItemInner}>
                                    <div className={classes.imageListImageContainer}>
                                        <img src={f.url} alt={"Attach Thumbnail"} />
                                        {
                                            onUploadRemove && (
                                                <div className={classes.imageListItemActions}>
                                                    <MuiClickable onClick={() => { this.removeAttach(v); }}>
                                                        <MuiColor variant="error"><FontAwesomeIcon icon={faTrash} fixedWidth /></MuiColor>
                                                    </MuiClickable>
                                                </div>
                                            )
                                        }
                                    </div>
                                </div>
                            </div>
                        );
                    })
                }
                {
                    (maxAttachFiles == null || maxAttachFiles < files.length) &&
                    this.renderAlbumAddImage()
                }
            </div>
        );
    }

    ///
    private renderAttach() {
        if ((this.props.selectedType == AttachType.Link || this.props.selectedType == AttachType.Publication) && this.props.attach != null) {
            return <RenderMetadata metadata={this.props.attach as ContentMetadataGetDto} />;
        }

        if (this.props.selectedType == AttachType.Image) {
            return this.renderImageAlbum();
        }

        if (this.props.selectedType == AttachType.Video) {
            return <RenderVideo file={(this.props.attach[0] as FileThumbnaildGetDto).mainFile} />;
        }

        if (this.props.selectedType == AttachType.File) {
            return <RenderFile file={(this.props.attach[0] as FileThumbnaildGetDto).mainFile} />;
        }

        return null;
    }

    ///
    private loadImage = (image: ImageSelectionResult) => {

        if (image == null) {
            this.setState({ dialog: null });
        }
        else {
            var finalImage = image.source;

            this.setState({ isLoading: true, dialog: null, }, () => {

                this.asyncUploader.uploadImage(finalImage, AspectRatio.Wide);
            });

        }
    };

    ///
    private loadAttachStarted = () => {
        if (this.props.onUploadStatusChange) this.props.onUploadStatusChange("uploading");
    };

    ///
    private loadAttachProgress = (progress: number) => {
        this.setState({ progressBar: progress });
    };

    ///
    private loadAttachSuccess = (data: AsyncUploadModel) => {
        if (this.props.onUploadStatusChange) this.props.onUploadStatusChange("uploaded");

        if (data.success) {
            this.setState({ progressBar: null, lastFileUploaded: data, isLoading: false });
            if (this.props.onUploadComplete) this.props.onUploadComplete(data);
        }
        else {
            this.setState(
                { progressBar: null, lastFileUploaded: null, isLoading: false },
                () => this.props.snackbar.showMessage(this.props.t("Views.Publication.Components.HandleAttach.UploadProccessFailed"), "error")
            );
        }
    };

    ///
    private loadAttachError = (thrown: any) => {
        if (this.props.onUploadStatusChange) this.props.onUploadStatusChange("canceled");

        if (this.asyncUploader.isCancel(thrown)) {
            this.props.snackbar.showMessage(this.props.t("Views.Publication.Components.HandleAttach.UploadCanceled"), "info");
        }
        else {
            let message = this.props.t("Views.Publication.Components.HandleAttach.UploadError");
            if (thrown.handled == false) {
                var e = (thrown.handledError) as ApiException;
                if (e.exceptionCode == 409) message = e.exceptionMessage;
            }
            this.props.snackbar.showMessage(message, "error");
        }

        this.setState({ progressBar: null, lastFileUploaded: null, isLoading: false });
    };

    ///
    private loadAttach(e: File) {
        //if (e.type.indexOf("image") == 0) {
        //    this.handleImageSelection(e);
        //}
        //else {
        //}
        this.setState({ isLoading: true }, () => {

            const formData = new FormData();
            formData.append('file', e, e.name);
            formData.append('createThumbnailAspectRatio', AspectRatio[AspectRatio.Wide]);

            this.asyncUploader.uploadForm(formData);
        });
    }

    ///
    private removeAttach(fileOrContent: FileThumbnaildGetDto | ContentMetadata) {

        if (!this.props.onUploadRemove) return;

        this.setState(
            {
                dialog: (
                    <MuiConfirmation
                        message={this.props.t("Views.Publication.Components.HandleAttach.ConfirmationRemoveAttachMessage")}
                        onClose={
                            (result) => {

                                this.setState(
                                    { dialog: null },
                                    () => {

                                        if (result) {

                                            if ((fileOrContent as FileThumbnaildGetDto).mainFile) {
                                                const attach = fileOrContent as FileThumbnaildGetDto;

                                                var files = [attach.mainFile.fileId];
                                                if (attach.thumbnail != null) files.push(attach.thumbnail.fileId);

                                                this.asyncUploader.delete(files)
                                                    .then(() => {
                                                        this.props.onUploadRemove(attach);
                                                    });
                                            }
                                            else {
                                                this.props.onUploadRemove(fileOrContent);
                                            }
                                        }

                                    }
                                );
                            }
                        }
                    />
                )
            }
        );
    }

    ///
    private handlePublication = () => {
        this.setState({
            dialog: (
                <PublicationSelectionDrawer
                    title={this.props.t("Common.Literals.PublicationSelection")}
                    excludeOptions={
                        {
                            excludePublications: this.props.publicationId == null ? null : [this.props.publicationId],
                            excludePublicationsType: [PublicationType.Poll, PublicationType.Survey, PublicationType.Course]
                        }
                    }
                    onSelect={
                        (result) => {
                            if (result != null) {
                                const c: ContentMetadataGetDto = {
                                    provider: "publication",
                                    uId: result.publication.publicationId,

                                    // Este dados são preenchidos para que o render possa ser visualizado.
                                    // Não há necessidade de salvar estas informações.
                                    title: result.publication.title,
                                    image: result.publication.firstImageDataOrUrl,
                                    tumbnailUrl: result.publication.firstImageDataOrUrl,
                                    authorName: result.publication.creatorName,
                                    url: getPublicationUrl(result.publication.publicationId, result.publication.publicationType)
                                };
                                if (this.props.onMetadataReceived) this.props.onMetadataReceived(c);
                            }
                            return false;
                        }
                    }
                    onClose={() => this.setState({ dialog: null })}
                />
            )
        });
    };

    ///
    private handlePool = () => {
        this.props.onPool();
    };

    ///
    private handleAttachTypeButtonClick(acceptFiles: string[]) {
        this.fileInputRef.accept = acceptFiles?.join(",");
        this.fileInputRef.click();
    }

    ///
    private handleImageSelection(initialFile: File = null) {
        this.setState({
            dialog: (
                <ImageSelectionDialog
                    acceptedFiles={MediaUtils.AcceptImages()}
                    initialSource="library"
                    initialFile={initialFile}
                    allowEdit={false}
                    dimensions={WidePicture}
                    onClose={this.loadImage}
                />
            )
        });
    }

    ///
    private renderLeft() {
        const { additionalLeftButton, acceptedTypes, selectedType, attach, readOnly } = this.props;

        return (
            <div>
                {
                    additionalLeftButton
                }

                {
                    this.props.selectedType == AttachType.Link &&
                    <MuiRegularButton simple disabled><FontAwesomeIcon icon={faLink} fixedWidth /></MuiRegularButton>
                }

                {
                    acceptedTypes.includes(AttachType.Video) && (selectedType == null || selectedType == AttachType.Video) &&
                    <MuiRegularButton
                        simple
                        title={this.props.t("Common.AttachType.Video")}
                        color="secondary"
                        disabled={selectedType != null || readOnly}
                        onClick={() => { this.handleAttachTypeButtonClick(MediaUtils.AcceptAzureVideos()); }}>
                        <FontAwesomeIcon icon={faVideo} fixedWidth />
                    </MuiRegularButton>
                }

                {
                    acceptedTypes.includes(AttachType.Image) && (selectedType == null || selectedType == AttachType.Image) &&
                    <MuiRegularButton
                        simple
                        title={this.props.t("Common.AttachType.Image")}
                        color="secondary"
                        disabled={selectedType != null || readOnly}
                        onClick={() => { this.handleImageSelection(); }}>
                        <FontAwesomeIcon icon={faImage} fixedWidth />
                    </MuiRegularButton>
                }

                {
                    acceptedTypes.includes(AttachType.File) && (selectedType == null || selectedType == AttachType.File) &&
                    <MuiRegularButton
                        simple
                        title={this.props.t("Common.AttachType.File")}
                        color="secondary"
                        disabled={selectedType != null || readOnly}
                        onClick={() => { this.handleAttachTypeButtonClick(MediaUtils.AcceptFiles()); }}>
                        <FontAwesomeIcon icon={faFileUpload} fixedWidth />
                    </MuiRegularButton>
                }

                {
                    acceptedTypes.includes(AttachType.Publication) && this.props.onMetadataReceived && (selectedType == null || selectedType == AttachType.Publication) &&
                    <MuiRegularButton
                        simple
                        title={this.props.t("Common.AttachType.Publication")}
                        color="secondary"
                        disabled={selectedType != null || readOnly}
                        onClick={this.handlePublication}>
                        <FontAwesomeIcon icon={faPaperclip} fixedWidth />
                    </MuiRegularButton>
                }

                {
                    selectedType != AttachType.Image && selectedType != null && attach != null &&
                    (
                        <MuiRegularButton
                            simple
                            color="error"
                            title={this.props.t("Common.Buttons.RemoveAttachTitle")}
                            disabled={readOnly}
                            onClick={() => {
                                var content = attach as ContentMetadata;
                                if (content.provider) this.removeAttach(content);
                                else this.removeAttach(attach[0] as FileThumbnaildGetDto);
                            }} >
                            <FontAwesomeIcon icon={faTrash} fixedWidth />
                        </MuiRegularButton>
                    )
                }

                <input
                    type="file"
                    style={{ display: "none" }}
                    ref={(ref) => this.fileInputRef = ref}
                    onChange={
                        (e) => {
                            this.loadAttach(e.target.files[0]);
                            this.fileInputRef.value = null;
                        }
                    }
                />

            </div>
        );
    }

    ///
    private renderRight() {

        const { acceptedTypes, classes, readOnly, selectedType } = this.props;
        if (readOnly) return null;

        const acceptFiles: string[] = [];
        if ((selectedType == null || selectedType == AttachType.Image) && acceptedTypes.includes(AttachType.Image)) acceptFiles.push(...MediaUtils.AcceptImages());
        if (selectedType == null && acceptedTypes.includes(AttachType.Video)) acceptFiles.push(...MediaUtils.AcceptAzureVideos());
        if (selectedType == null && acceptedTypes.includes(AttachType.File)) acceptFiles.push(...MediaUtils.AcceptFiles());
        if (acceptFiles.length == 0) return null;

        return (

            <div>

                {
                    (selectedType == null || selectedType == AttachType.Image) && !this.state.isLoading &&
                    <Dropzone
                        accept={acceptFiles}
                        multiple={false}
                        onDropRejected={
                            () => this.props.snackbar.showMessage(this.props.t("Components.AttachSelection.FileNotAccept"), "error")
                        }
                        onDropAccepted={
                            (e) => {

                                if (this.props.selectedType != null && this.props.selectedType != AttachType.Image) {

                                    this.setState({
                                        dialog: (
                                            <MuiConfirmation
                                                message={this.props.t("Views.Publication.Components.HandleAttach.ConfirmationRemoveOldContentMessage")}
                                                onClose={
                                                    (result) => {

                                                        this.setState(
                                                            { dialog: null },
                                                            () => { if (result) this.loadAttach(e[0]); }
                                                        );
                                                    }
                                                }
                                            />
                                        )
                                    });
                                }
                                else {
                                    this.loadAttach(e[0]);
                                }
                            }
                        }
                    >
                        {
                            ({ getRootProps, getInputProps }) => {
                                return (
                                    <div
                                        {...getRootProps()}
                                        className={classes.dropzone}
                                    >
                                        <input {...getInputProps()} />
                                        <span>{this.props.dropzoneText || this.props.t("Components.AttachSelection.DropzoneText")}</span>
                                    </div>
                                );
                            }
                        }
                    </Dropzone>
                }

                {
                    this.state.progressBar != null
                        ? (
                            <>
                                <div className={classes.progressBar}>
                                    <LinearProgress variant="determinate" value={this.state.progressBar} />
                                </div>
                                <span>{this.state.progressBar + "%"}</span>
                                <ButtonCancel
                                    link
                                    simple
                                    onClick={
                                        () => {
                                            this.asyncUploader.cancel('Operation canceled by the user.');
                                        }
                                    }
                                />
                            </>
                        )
                        : null
                }
            </div>
        );
    }

    ///
    public componentDidUpdate() {
        setTimeout(() => this.addBoxRef && this.addBoxRef.scrollIntoView(false), 100);
    }

    ///
    public render() {
        const { classes } = this.props;

        return (
            <>
                <div>
                    <GridEx container className={classes.container} justifyContent="center" alignItems="flex-start">
                        <GridEx item>
                            {this.renderLeft()}
                        </GridEx>
                        <GridEx item className={GlobalClassKeys.pushRight}>
                            {this.renderRight()}
                        </GridEx>
                    </GridEx>
                </div>
                {this.renderAttach()}
                {this.state.dialog}
            </>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(styles)(HandleAttach)));